import { useTranslation } from "react-i18next";
import { Control, Controller, FieldValues, Path, useController } from "react-hook-form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ControlledUnifiedInput } from "./controlled-unified-input";
import { InputType } from "./unified-input";

interface ControlledRestrictionRowProps<T extends FieldValues> {
  control: Control<T>;
  booleanName: Path<T>;
  valueName: Path<T>;
  label: string;
  placeholder: string;
  disabled?: boolean;
  inputType?: InputType;
  rightElement?: React.ReactNode;
}

export const ControlledRestrictionRow = <T extends FieldValues>({
  control,
  booleanName,
  valueName,
  label,
  placeholder,
  disabled,
  inputType = "integer",
  rightElement,
}: ControlledRestrictionRowProps<T>) => {
  const { t } = useTranslation("shared");
  const { field: valueField } = useController({ control, name: valueName });

  return (
    <Controller
      control={control}
      name={booleanName}
      render={({ field }) => (
        <div className="flex items-center gap-4">
          <span className="w-44 shrink-0 text-sm font-medium text-gray-600">{label}</span>
          <RadioGroup
            value={field.value ? "limited" : "unlimited"}
            onValueChange={(v) => {
              const isLimited = v === "limited";
              field.onChange(isLimited);
              if (!isLimited) {
                valueField.onChange(0);
              }
            }}
            disabled={disabled}
            className="flex items-center gap-3"
          >
            <div className="flex items-center gap-1.5">
              <RadioGroupItem value="unlimited" id={`${booleanName}-unlimited`} />
              <Label
                htmlFor={`${booleanName}-unlimited`}
                className="cursor-pointer text-sm font-medium"
              >
                {t("restrictionRow.unlimited")}
              </Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem value="limited" id={`${booleanName}-limited`} />
              <Label
                htmlFor={`${booleanName}-limited`}
                className="cursor-pointer text-sm font-medium"
              >
                {t("restrictionRow.limitedTo")}
              </Label>
            </div>
          </RadioGroup>
          <div className="flex flex-1 items-center gap-2">
            <ControlledUnifiedInput
              control={control}
              name={valueName}
              inputType={inputType}
              placeholder={placeholder}
              disabled={disabled || !field.value}
              labelStyle="none"
              wrapperClassName="w-32 h-[36px]"
            />
            {rightElement}
          </div>
        </div>
      )}
    />
  );
};
